import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ASSETS } from '../mock';
import Navbar from './Navbar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import LiveOnlineBadge from './LiveOnlineBadge';

const Hero = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSignup = () => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Missing fields', { description: 'Please enter your email and password.' });
      return;
    }
    if (password.length < 6) {
      toast.error('Password too short', { description: 'Use at least 6 characters.' });
      return;
    }
    if (!agreed) {
      toast.error('Agreement required', { description: 'Please agree to the terms to sign up.' });
      return;
    }
    setLoading(true);
    try {
      const user = await register(email, password);
      toast.success('Welcome to Flirty!', { description: 'Your account is ready.' });
      navigate(user.has_profile ? '/browse' : '/profile/setup');
    } catch (err) {
      toast.error('Sign up failed', { description: err.response?.data?.detail || 'Try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative min-h-[860px] md:min-h-[820px] w-full overflow-hidden bg-fnl-dark">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${ASSETS.heroBg})` }}
      />
      <div className="absolute inset-0 hero-gradient-overlay" />

      {/* Decorative side gradients */}
      <img src={ASSETS.bgLeftSite} alt="" className="absolute left-0 top-0 h-full opacity-50 pointer-events-none" />
      <img src={ASSETS.bgRightSite} alt="" className="absolute right-0 top-0 h-full opacity-60 pointer-events-none" />

      {/* Floating hearts */}
      <div className="absolute top-32 left-[18%] animate-float">
        <div className="w-14 h-14 rounded-full bg-fnl-pink/90 flex items-center justify-center shadow-2xl shadow-pink-500/50">
          <img src={ASSETS.smallHeart} alt="" className="w-6 h-6 brightness-0 invert" />
        </div>
      </div>
      <div className="absolute bottom-44 left-[10%] animate-float-slow">
        <div className="w-20 h-20 rounded-full bg-fnl-pink flex items-center justify-center shadow-2xl shadow-pink-500/60">
          <img src={ASSETS.heartMainBanner} alt="" className="w-9 h-9 brightness-0 invert" />
        </div>
      </div>

      <Navbar />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-32 md:pt-36 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Left text */}
          <div className="animate-fade-up">
            <LiveOnlineBadge />
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-fnl-pink leading-[1.05] tracking-tight">
              Discover the<br />Perfect match<br />for you
            </h1>
          </div>

          {/* Right Sign-up box */}
          <div className="bg-fnl-purple/95 backdrop-blur-md rounded-2xl p-7 md:p-8 shadow-2xl border border-purple-900/40">
            <h2 className="text-2xl font-bold text-white mb-1">Discover the Perfect match for you</h2>
            <p className="text-purple-200 text-sm mb-5 mt-2">Sign Up for free with:</p>

            <button
              onClick={handleGoogleSignup}
              className="w-full h-12 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-md flex items-center justify-center gap-3 mb-3 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.7l6.2 5.2c-.4.4 6.6-4.8 6.6-14.9 0-1.3-.1-2.3-.4-3.5z"/></svg>
              Sign up with Google
            </button>

            <button
              onClick={() => toast('Yahoo Sign-in', { description: 'Yahoo OAuth not available in this demo.' })}
              className="w-full h-12 bg-yahoo hover:bg-yahoo text-white font-medium rounded-md flex items-center justify-center gap-3 mb-5 transition-colors"
            >
              <span className="font-bold text-lg italic">Y!</span>
              Sign in with Yahoo
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-purple-700/60" />
              <span className="text-purple-200 text-sm">or Sign Up for free</span>
              <div className="flex-1 h-px bg-purple-700/60" />
            </div>

            <form onSubmit={handleSignUp} className="space-y-3">
              <div>
                <label className="text-white text-xs font-medium mb-1.5 block">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 bg-white border-0 text-gray-800"
                />
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1.5 block">Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 bg-white border-0 text-gray-800"
                />
              </div>

              <div className="flex items-start gap-2 pt-2">
                <Checkbox
                  id="agree"
                  checked={agreed}
                  onCheckedChange={setAgreed}
                  className="mt-1 border-purple-300 data-[state=checked]:bg-fnl-pink data-[state=checked]:border-fnl-pink"
                />
                <label htmlFor="agree" className="text-purple-100 text-[11px] leading-tight cursor-pointer">
                  By signing up for our Services you hereby state that: (a) You are above the age of eighteen (18) or the age of majority where you live to use our adult entertainment website (b) You have read and agreed our <span className="text-fnl-pink underline">Terms and Conditions</span>, our <span className="text-fnl-pink underline">Privacy Policy</span> and our <span className="text-fnl-pink underline">Cookies Policy</span>.
                </label>
              </div>

              <Button type="submit" disabled={loading} className="w-full h-12 bg-fnl-pink hover:bg-fnl-pink text-white font-semibold rounded-md btn-shadow-pink text-base mt-3">
                {loading ? 'Creating account...' : 'Sign Up for free'}
              </Button>

              <p className="text-center text-purple-200 text-sm pt-1">
                Already a member? <a href="/login" className="text-fnl-pink hover:underline font-semibold">Log in</a>
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
